import { HISTORY_DEPTH_MAX, HISTORY_DEPTH_MIN } from '../constants/datasetGenerationLimits';

/** Display value for a numeric input that may be cleared while editing. */
export function numberInputDisplay(value: unknown, fallback?: number): string {
  if (value === '') return '';
  if (value == null) {
    return fallback != null ? String(fallback) : '';
  }
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return fallback != null ? String(fallback) : '';
  }
  return String(n);
}

/** Resolve a stored draft (number, string, or empty) to a finite number for API/validation. */
export function resolveNumberInput(value: unknown, fallback: number): number {
  if (value === '' || value == null) return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Keep decimal draft within [min, max] while the user types. */
export function sanitizeBoundedDecimalDraft(raw: string, min: number, max: number): string {
  const trimmed = raw.trim();
  if (trimmed === '') return '';

  const normalized = trimmed.replace(',', '.');
  if (!/^\d*\.?\d*$/.test(normalized)) {
    return normalized.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');
  }
  if (normalized === '.') return '0.';

  const num = Number(normalized);
  if (!Number.isFinite(num)) return '';

  if (normalized.endsWith('.') && num >= min && num <= max) {
    return normalized;
  }

  if (num > max) return String(max);
  if (num < min) return String(min);
  return normalized;
}

/** Clamp and snap to step on blur. */
export function commitBoundedDecimalDraft(raw: string, min: number, max: number, step: number): string {
  const trimmed = raw.trim();
  if (trimmed === '') return '';
  const num = Number(trimmed.replace(',', '.'));
  if (!Number.isFinite(num)) return '';
  const clamped = Math.min(max, Math.max(min, num));
  const rounded = Math.round(clamped / step) * step;
  const decimals = String(step).includes('.') ? String(step).split('.')[1]?.length ?? 1 : 0;
  return String(Number(rounded.toFixed(decimals)));
}

/** Keep positive integer draft within [min, max] while the user types. */
export function sanitizePositiveIntegerDraft(raw: string, min: number, max: number): string {
  const trimmed = raw.trim();
  if (trimmed === '') return '';
  const digitsOnly = trimmed.replace(/\D/g, '');
  if (digitsOnly === '') return '';
  const num = Number(digitsOnly);
  if (!Number.isFinite(num)) return '';
  if (num > max) return String(max);
  return digitsOnly;
}

/** Clamp integer on blur. */
export function commitPositiveIntegerDraft(raw: string, min: number, max: number): string {
  const trimmed = raw.trim();
  if (trimmed === '') return '';
  const num = Number(trimmed.replace(/\D/g, ''));
  if (!Number.isFinite(num)) return '';
  const clamped = Math.min(max, Math.max(min, Math.round(num)));
  return String(clamped);
}

/** Resolve history depth for API payloads (empty → fallback). */
export function clampHistoryDepth(value: unknown, fallback: number): number {
  if (value === '' || value == null) return fallback;
  const n = typeof value === 'string' ? parseInt(value, 10) : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(HISTORY_DEPTH_MAX, Math.max(HISTORY_DEPTH_MIN, Math.round(n)));
}
